import classNames from "classnames";
import { type FC } from "react";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { selectFolderItems } from "../../redux/selectors";
import { toggleAccordion } from "../../redux/slice";
import styles from "../../styles.module.css";
import type { MenuItem, MenuItems } from "../../types";
import { SidebarFolderItem } from "./SidebarFolderItem";
import { SidebarItem } from "./SidebarItem";
import { StorageScope } from "../../../../services/api/instances/storageApi/types/service_types";

interface Props {
  menuItems: MenuItems;
  onSelect: (id: string) => void;
  onDelete?: (id: string, name: string) => void;
  selectedId?: string | null;
  defaultName?: string;
  onOpenModal?: (id: string) => void;
  onOpenModalNameChange?: (id: string, name: string) => void;
  isTransfer?: boolean;
  isSendDisabled?: boolean;
  onOpenMenu?: (
    event: React.MouseEvent<HTMLElement>,
    id: string,
    name: string,
  ) => void;
  onCopyObject?: (id: string) => void;
  hasMenu?: boolean;
  service?: StorageScope;
}

export const SidebarList: FC<Props> = ({
  menuItems,
  onSelect,
  selectedId,
  defaultName,
  onDelete,
  onOpenModal,
  onOpenModalNameChange,
  isTransfer,
  onOpenMenu,
  hasMenu,
  onCopyObject,
  isSendDisabled,
  service,
}) => {
  const dispatch = useAppDispatch();
  const folderItems = useAppSelector(selectFolderItems);

  const handleToggle = (id: string) => {
    dispatch(toggleAccordion(id));
  };

  return (
    <>
      {menuItems?.map((item: MenuItem) => {
        if (item?.isFolder) {
          return (
            <SidebarFolderItem
              key={item.id}
              selectedId={item.id}
              onSelect={() => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                !isSendDisabled && item.id && onSelect(item.id);
              }}
              itemName={item.name}
              isOpen={folderItems?.includes(item.id)}
              onToggle={() => handleToggle(item.id)}
              onDelete={() => onDelete && onDelete(item.id, item.name)}
              onOpenModalNameChange={() =>
                onOpenModalNameChange &&
                onOpenModalNameChange(item.id, item.name)
              }
              isFolderEmpty={!item?.children?.length}
              isSendDisabled={isSendDisabled}
              child={item?.children?.map((child) => (
                <SidebarItem
                  key={child.id}
                  onSelect={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    !isSendDisabled && child.id && onSelect(child.id);
                  }}
                  selectedId={selectedId}
                  isTransfer={isTransfer}
                  itemName={!child.name ? defaultName : child.name}
                  onDelete={onDelete && (() => onDelete(child.id, child.name))}
                  isSendDisabled={isSendDisabled}
                  onOpenModal={() => onOpenModal && onOpenModal(child.id)}
                  onOpenMenu={(e) =>
                    onOpenMenu && onOpenMenu(e, child.id, child.name)
                  }
                  onCopyObject={onCopyObject && (() => onCopyObject(child?.id))}
                  hasMenu={hasMenu}
                  service={service}
                  iconTagType={child.iconTagType}
                  className={classNames(styles["folder-item"], {
                    [styles.selected]: child.id === selectedId,
                    [styles.arrow]: child.id === selectedId && !onDelete,
                    [styles["select-bg"]]: child.id === selectedId,
                  })}
                />
              ))}
              className={classNames(styles["list-folder"], {
                [styles.selected]: item.id === selectedId,
                [styles.arrow]: item.id === selectedId && !onDelete,
                [styles["folder-selected"]]: item?.children?.find(
                  (child) => child.id === selectedId,
                ),
                [styles["select-bg"]]: item.id === selectedId,
              })}
            />
          );
        }
        if (!item?.children?.length) {
          return (
            <SidebarItem
              key={item.id}
              onSelect={() => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                !isSendDisabled && item.id && onSelect(item.id);
              }}
              selectedId={selectedId}
              isTransfer={isTransfer}
              itemName={!item.name ? defaultName : item.name}
              onDelete={onDelete && (() => onDelete(item.id, item.name))}
              isSendDisabled={isSendDisabled}
              onOpenModal={() => onOpenModal && onOpenModal(item.id)}
              onOpenMenu={(e) =>
                onOpenMenu && onOpenMenu(e, item.id, item.name)
              }
              hasMenu={hasMenu}
              service={service}
              iconTagType={item.iconTagType}
              onCopyObject={onCopyObject && (() => onCopyObject(item?.id))}
              className={classNames(styles["list-item"], {
                [styles.selected]: item.id === selectedId,
                [styles.arrow]: item.id === selectedId && !onDelete,
                [styles["select-bg"]]: item.id === selectedId,
              })}
            />
          );
        }
      })}
    </>
  );
};
